import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  kanban,
  tasks as tasksApi,
  tracks as tracksApi,
  withCloseOutOverride,
} from "../../lib/api";
import { notify } from "../../lib/notifications";
import { useUI } from "../../lib/store";
import type { Task, TaskStatus, Track } from "../../lib/types";
import { TASK_STATUSES } from "../../lib/types";
import { StatusDot, statusLabel, statusRailColor } from "../shared/StatusDot";
import { TaskCard } from "./TaskCard";
import { DoneReviewModal } from "./DoneReviewModal";
import { BatchConfirmBar } from "./BatchConfirmBar";
import { BulkAddToTrackBar } from "./BulkAddToTrackBar";
import { ChevronDown, ChevronRight } from "../shared/Icon";
import { Skeleton } from "../shared/Skeleton";
import { cn } from "../../lib/cn";

interface ColumnProps {
  status: TaskStatus;
  tasks: Task[];
  loading?: boolean;
  trackById: Map<string, Track>;
}

// `verifying` is never a column: those tasks show under In Progress.
const COLUMN_EMPTY: Partial<Record<TaskStatus, string>> = {
  backlog: "Capture an idea",
  todo: "Drag tasks here",
  in_progress: "Nothing in progress",
  needs_input: "All clear",
  in_review: "Nothing waiting on you",
  done: "Recently shipped",
  interrupted: "No failures",
};

function Column({ status, tasks, loading, trackById }: ColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col:${status}`,
    data: { status },
  });

  // Standalone tasks render flat; a track's tasks are pulled out and grouped
  // under their track's collapsible header so they read as subordinate to it.
  const { standalone, groups } = useMemo(() => {
    const standalone: Task[] = [];
    const groups = new Map<string, Task[]>();
    for (const t of tasks) {
      if (!t.track_id) {
        standalone.push(t);
      } else {
        const arr = groups.get(t.track_id) ?? [];
        arr.push(t);
        groups.set(t.track_id, arr);
      }
    }
    for (const arr of groups.values()) {
      arr.sort((a, b) => (a.track_ordinal ?? 0) - (b.track_ordinal ?? 0));
    }
    return { standalone, groups };
  }, [tasks]);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Sortable items must match what's actually rendered — drop the tasks inside
  // collapsed groups so dnd-kit doesn't measure missing nodes.
  const sortableIds = useMemo(() => {
    const ids = standalone.map((t) => t.id);
    for (const [tid, arr] of groups) {
      if (!collapsed.has(tid)) ids.push(...arr.map((t) => t.id));
    }
    return ids;
  }, [standalone, groups, collapsed]);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex-1 min-w-[22rem] flex flex-col",
        "border-r border-border-subtle last:border-r-0",
        "transition-colors duration-fast ease-out",
        isOver && "bg-accent-soft",
      )}
    >
      <div className="px-3 py-2.5 flex items-center justify-between sticky top-0 z-10 bg-bg-canvas border-b border-border-subtle">
        <div className="flex items-center gap-2">
          <StatusDot status={status} size={6} />
          <span className="text-caption text-text-tertiary">
            {statusLabel(status)}
          </span>
          <CountPill count={tasks.length} />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-2">
        <SortableContext
          id={status}
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          {loading && (
            <>
              <Skeleton height={68} className="mb-2" />
              <Skeleton height={68} />
            </>
          )}
          {!loading && tasks.length === 0 && (
            <div className="py-10 text-center text-small text-text-tertiary">
              {COLUMN_EMPTY[status]}
            </div>
          )}
          {standalone.map((t) => (
            <TaskCard key={t.id} task={t} />
          ))}
          {[...groups.entries()].map(([trackId, groupTasks]) => (
            <TrackSection
              key={trackId}
              trackId={trackId}
              track={trackById.get(trackId)}
              status={status}
              tasks={groupTasks}
              collapsed={collapsed.has(trackId)}
              onToggle={() =>
                setCollapsed((prev) => {
                  const next = new Set(prev);
                  next.has(trackId) ? next.delete(trackId) : next.add(trackId);
                  return next;
                })
              }
            />
          ))}
        </SortableContext>
      </div>
    </div>
  );
}

/**
 * A track's tasks within one status column, grouped under a collapsible header
 * (title + count) with a track-colored rail so they read as subordinate to the
 * track rather than mixed in with standalone cards. In the In-Review column the
 * header carries the explicit operator "Confirm all" action for the whole track.
 */
function TrackSection({
  trackId,
  track,
  status,
  tasks,
  collapsed,
  onToggle,
}: {
  trackId: string;
  track: Track | undefined;
  status: TaskStatus;
  tasks: Task[];
  collapsed: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const confirmAll = useMutation({
    mutationFn: () =>
      withCloseOutOverride((o) => tasksApi.confirmTrack(trackId, o)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kanban"] });
      qc.invalidateQueries({ queryKey: ["tracks"] });
    },
    onError: (e) =>
      notify({
        severity: "error",
        title: "Confirm all failed",
        detail: e instanceof Error ? e.message : String(e),
      }),
  });
  // Run the whole track straight from the board. Manual dispatch is the human
  // approval gate, so it runs every task in ordinal order regardless of each
  // task's triage label — including the high-risk ones the autonomous loops
  // route to needs_human and won't auto-run. Only a planning/ready track is
  // dispatchable, and those have all-todo tasks, so this button surfaces once
  // (in the To Do group), the same single-action shape as Confirm all.
  const dispatchAll = useMutation({
    mutationFn: () => tracksApi.dispatch(trackId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kanban"] });
      qc.invalidateQueries({ queryKey: ["tracks"] });
    },
    onError: (e) =>
      notify({
        severity: "error",
        title: "Dispatch failed",
        detail: e instanceof Error ? e.message : String(e),
      }),
  });
  const dispatchable =
    track != null && (track.status === "planning" || track.status === "ready");
  const title = track?.title ?? "Track";
  return (
    <div className="rounded-md border border-border-subtle bg-bg-canvas">
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand track" : "Collapse track"}
          className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-sm text-text-tertiary hover:text-text-secondary hover:bg-bg-hover"
        >
          {collapsed ? <ChevronRight /> : <ChevronDown />}
        </button>
        <span
          className="flex-1 min-w-0 truncate text-small font-medium text-text-secondary"
          title={title}
        >
          {title}
        </span>
        {dispatchable && (
          <button
            type="button"
            onClick={() => dispatchAll.mutate()}
            disabled={dispatchAll.isPending}
            title="Dispatch the whole track now — runs every task in order, including ones triaged needs-human (manual dispatch is the human approval)"
            className={cn(
              "shrink-0 h-6 px-2 rounded-sm text-caption normal-case tracking-normal",
              "bg-accent-soft text-accent border border-accent-muted",
              "hover:bg-accent-muted/40 transition-colors duration-fast ease-out",
              "disabled:opacity-50 disabled:pointer-events-none",
            )}
          >
            {dispatchAll.isPending ? "Dispatching…" : "Dispatch"}
          </button>
        )}
        {status === "in_review" && (
          <button
            type="button"
            onClick={() => confirmAll.mutate()}
            disabled={confirmAll.isPending}
            title="Confirm and merge every open task in this track (in review + needs input)"
            className={cn(
              "shrink-0 h-6 px-2 rounded-sm text-caption normal-case tracking-normal",
              "bg-accent-soft text-accent border border-accent-muted",
              "hover:bg-accent-muted/40 transition-colors duration-fast ease-out",
              "disabled:opacity-50 disabled:pointer-events-none",
            )}
          >
            {confirmAll.isPending ? "Confirming…" : "Confirm all"}
          </button>
        )}
        <span className="shrink-0 text-mono text-text-tertiary">{tasks.length}</span>
      </div>
      {!collapsed && (
        <div
          className="ml-3 pl-2 pr-1.5 pb-1.5 space-y-2 border-l-2"
          style={{ borderColor: statusRailColor(status) }}
        >
          {tasks.map((t) => (
            <TaskCard key={t.id} task={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function CountPill({ count }: { count: number }) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center min-w-[20px]",
        "text-mono text-text-quaternary",
      )}
    >
      {count}
    </span>
  );
}

// Empty columns only register a single droppable — the column wrapper, whose
// corners sit at the top/bottom of the viewport. closestCorners then picks
// neighboring task cards (in the source column) over the empty target, so a
// drop on an empty column silently resolves to "same status" and no-ops.
// Pointer-first detection picks the column the cursor is actually inside;
// rectIntersection covers off-pointer drags. Within-column reorder isn't
// wired anywhere in this view, so biasing toward column wrappers is safe.
const kanbanCollision: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length) {
    const col = pointer.find((c) => String(c.id).startsWith("col:"));
    return col ? [col] : pointer;
  }
  return rectIntersection(args);
};

export function KanbanBoard() {
  const projectId = useUI((s) => s.selectedProjectId);
  const triageFilter = useUI((s) => s.triageFilter);
  const qc = useQueryClient();
  const queryKey = ["kanban", projectId ?? "all"] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => kanban.board(projectId ?? undefined),
    refetchInterval: 5_000,
  });

  // Track titles, so the board can group a track's tasks under a labeled,
  // collapsible section (subordinated to their track) instead of mixing them in
  // flat with standalone tasks.
  const { data: projectTracks } = useQuery({
    queryKey: ["tracks", projectId ?? "all"],
    queryFn: () => tracksApi.list(projectId ?? undefined),
    staleTime: 30_000,
  });
  const trackById = useMemo(
    () => new Map((projectTracks ?? []).map((t) => [t.id, t])),
    [projectTracks],
  );

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TaskStatus }) =>
      kanban.move(id, status),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData(queryKey);
      qc.setQueryData(queryKey, (old: typeof data) => {
        if (!old) return old;
        const columns = old.columns.map((c) => ({
          ...c,
          tasks: c.tasks.filter((t) => t.id !== id),
        }));
        let moved: Task | undefined;
        for (const c of old.columns) {
          moved = c.tasks.find((t) => t.id === id);
          if (moved) break;
        }
        if (moved) {
          for (const c of columns) {
            if (c.status === status) c.tasks.unshift({ ...moved, status });
          }
        }
        return { columns };
      });
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      // Surface the backend rejection (e.g. phantom in_progress: "no live
      // runtime and no recoverable CLI session; click Dispatch instead").
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : String(e));
    },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const allTasks = useMemo(() => {
    const map: Record<string, Task> = {};
    for (const col of data?.columns ?? []) {
      for (const t of col.tasks) {
        map[t.id] = t;
      }
    }
    return map;
  }, [data]);

  const onDragStart = (event: DragStartEvent) => {
    const t = allTasks[event.active.id as string];
    setActiveTask(t ?? null);
  };

  const [reviewTask, setReviewTask] = useState<Task | null>(null);

  const onDragEnd = (event: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;
    const taskId = active.id as string;
    const task = allTasks[taskId];
    if (!task) return;
    const overId = String(over.id);
    let newStatus: TaskStatus | null = null;
    if (overId.startsWith("col:")) {
      newStatus = overId.slice(4) as TaskStatus;
    } else if (allTasks[overId]) {
      newStatus = allTasks[overId].status;
    }
    if (newStatus && newStatus !== task.status) {
      // Reviewer's terminal — moving to `done` always routes through the
      // confirmation modal so the universal done-flow can fire.
      if (newStatus === "done") {
        setReviewTask(task);
        return;
      }
      // Phantom in_progress guard: a drag into In Progress is only
      // meaningful if there's a captured CLI session to resume. Without
      // one, the row would land in_progress with no runtime — exactly
      // the trap this gate exists to prevent. Backend enforces the same
      // rule; this avoids a wasted round-trip and a rolled-back drop.
      if (newStatus === "in_progress" && !task.cli_session_id) {
        // eslint-disable-next-line no-alert
        alert(
          "No saved CLI session for this task — click Dispatch instead.",
        );
        return;
      }
      move.mutate({ id: taskId, status: newStatus });
    }
  };

  return (
    <>
    <DndContext
      sensors={sensors}
      collisionDetection={kanbanCollision}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="relative flex-1 flex flex-col min-h-0">
      <div className="flex-1 flex overflow-x-auto">
        {TASK_STATUSES.map((status) => {
          const col = data?.columns.find((c) => c.status === status);
          // Shelved tasks stay visible but sink to the bottom of every column,
          // marked by the per-card "shelved" chip the way priority is marked
          // by P0/P1/P2/P3. Stable partition: active first, shelved after.
          const colTasks = triageFilter
            ? (col?.tasks ?? []).filter((t) => t.triage_status === triageFilter)
            : (col?.tasks ?? []);
          const active = colTasks.filter((t) => t.lifecycle !== "shelved");
          const shelved = colTasks.filter((t) => t.lifecycle === "shelved");
          const ordered = [...active, ...shelved];
          return (
            <Column
              key={status}
              status={status}
              tasks={ordered}
              loading={isLoading}
              trackById={trackById}
            />
          );
        })}
      </div>
      <BatchConfirmBar tasks={allTasks} />
      <BulkAddToTrackBar tasks={allTasks} />
      </div>
      <DragOverlay>
        {activeTask ? (
          <div className="relative bg-bg-surface rounded-md border border-border-strong pl-3.5 pr-3 py-3 w-[22rem] shadow-lg">
            <span
              aria-hidden="true"
              className="absolute inset-y-0 left-0 w-[3px] rounded-l-md"
              style={{ background: statusRailColor(activeTask.status) }}
            />
            <div className="text-body text-text-primary truncate">
              {activeTask.title}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
    {reviewTask && (
      <DoneReviewModal task={reviewTask} onClose={() => setReviewTask(null)} />
    )}
    </>
  );
}
